<template>
  <div v-if="visible" class="forgot-password-overlay" @click.self="close">
    <div class="forgot-password-dialog">
      <!-- colored card behind -->
      <div class="forgot-password-dialog-bg"></div>
      <!-- main white card -->
      <div class="forgot-password-dialog-inner">
        <button class="forgot-password-close" @click="close">&times;</button>

        <div class="forgot-password-icon">
          <v-icon>mdi-lock-reset</v-icon>
        </div>

        <h2 class="forgot-password-title">Forgot Password?</h2>
        <p class="forgot-password-message">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        
        <v-form ref="form" v-model="valid" lazy-validation class="forgot-password-form">
          <v-text-field
            v-model="email"
            label="Email Address"
            variant="solo"
            rounded
            flat
            bg-color="#f5f5f5"
            hide-details
            prepend-inner-icon="mdi-email"
            :rules="[
              v => !!v || 'Email is required',
              v => /.+@.+\..+/.test(v) || 'Must be a valid email'
            ]"
            class="forgot-password-input"
          ></v-text-field>
          
          <button 
            :class="['forgot-password-button', { 'loading': loading }]" 
            @click="sendResetEmail"
            :disabled="!valid || loading"
          >
            <span v-if="!loading">Send Reset Link</span>
            <span v-else>Sending...</span>
          </button>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script>
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';

export default {
  name: "ForgotPasswordDialog",
  props: {
    visible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      email: '',
      valid: false,
      loading: false
    };
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
      this.resetForm();
    },
    
    async sendResetEmail() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      
      try {
        await sendPasswordResetEmail(auth, this.email);
        
        // Show success message
        this.$emit('reset-email-sent', {
          success: true,
          message: 'Password reset email sent successfully! Please check your inbox.'
        });
        
        this.close();
        
      } catch (error) {
        console.error('Error sending reset email:', error);
        
        // Show error message
        this.$emit('reset-email-sent', {
          success: false,
          message: this.getErrorMessage(error.code)
        });
      } finally {
        this.loading = false;
      }
    },
    
    getErrorMessage(errorCode) {
      switch (errorCode) {
        case 'auth/user-not-found':
          return 'No account found with this email address.';
        case 'auth/invalid-email':
          return 'Please enter a valid email address.';
        case 'auth/too-many-requests':
          return 'Too many requests. Please try again later.';
        default:
          return 'Failed to send reset email. Please try again.';
      }
    },
    
    resetForm() {
      this.email = '';
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    }
  },
  
  watch: {
    visible(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped>
.forgot-password-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.forgot-password-dialog {
  position: relative;
  width: 500px;
  max-width: 90vw;
  min-height: 300px;
}

.forgot-password-dialog-bg {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 96%;
  height: 100%;
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  background-color: #002060;
}

.forgot-password-dialog-inner {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}

.forgot-password-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
}

.forgot-password-icon {
  position: relative;
  margin: 0 auto 24px auto;
  margin-top: -16%;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInCircle 0.3s ease-out forwards;
  background-color: #002060;
}

.forgot-password-icon .v-icon {
  font-size: 40px;
  color: #ffffff;
}

.forgot-password-icon::before {
  content: '';
  position: absolute;
  width: 100px;
  height: 100px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  opacity: 0.2;
  animation: pulse 2s ease-in-out infinite;
  background-color: #002060;
}

@keyframes fadeInCircle {
  from {
    opacity: 0;
    transform: scale(0.7);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
  }
}

.forgot-password-title {
  font-size: 2em;
  margin: 0 0 12px 0;
  color: #002060;
}

.forgot-password-message {
  font-size: 1.1em;
  margin: 0 0 24px 0;
  color: #666;
}

.forgot-password-form {
  text-align: left;
}

.forgot-password-input {
  margin-bottom: 24px;
}

.forgot-password-button {
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 1.1em;
  padding: 12px 24px;
  cursor: pointer;
  background-color: #002060;
  width: 100%;
  height: 48px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.forgot-password-button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.forgot-password-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.forgot-password-button.loading {
  cursor: not-allowed;
}
</style> 